/**
 * useVistaPermalink — Phase 7 Plan 07-06 Task 1 (HOST-04).
 *
 * Encodes and restores camera + era state via window.location.hash so any
 * visitor can copy a URL from their browser and share the exact view they're
 * looking at. Friend opens the link → lands at the same vista + era.
 *
 * Hash schema (07-RESEARCH.md Pattern 5 canonical):
 *   #v={vistaId}&e={era}                        short form at a vista zone
 *   #c={x},{y},{z},{yaw},{pitch}&e={era}        long form for free walk
 *   #e={era}                                    minimal (era only)
 *
 * Write debounce: 500ms so the hash doesn't repaint on every frame (would
 * pollute history + thrash the URL bar). Read is one-shot on mount.
 *
 * Vista-ID FREEZE CONTRACT (Pitfall 7 mitigation):
 *   The canonical vista IDs are `pavilion-view`, `dam-overlook`, `shore-south`.
 *   Renaming a vista ID breaks every permalink that encodes that ID. The
 *   ALIAS_MAP below is the only place where legacy IDs are accepted — any new
 *   rename MUST add an alias entry here. See
 *   .planning/phases/07-ux-accessibility-polish/deferred-items.md §"HOST-04
 *   vista-id contract" for the full rename protocol.
 *
 * Legacy alias:
 *   `vista-streetcar-stop` → `shore-south` (Phase 6 close-out carry-forward;
 *   the legacy id is retained on the era-photo asset filename for
 *   content-addressable continuity, but the runtime vistaZone id is
 *   `shore-south`).
 *
 * Design notes:
 *   - parseVistaHash + encodeVistaHash are PURE — no DOM, no THREE, no React.
 *     They're exported separately so unit tests can exercise the schema
 *     without booting a Canvas.
 *   - The effectful hook uses useFrame (per-frame) + a lastWrittenRef
 *     timestamp to implement the debounce without setTimeout, so R3F's render
 *     loop owns the timing. No setInterval cleanup concerns.
 *   - On mount the hook fires flyToVista(vistaId) if the scene store exposes
 *     it. If not (Phase 5/6 did NOT ship flyToVista), the hook logs a single
 *     warning and no-ops — the permalink gracefully degrades rather than
 *     crashing the scene.
 */
import { useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { useSceneStore } from '../store/useSceneStore';

export interface ParsedVistaState {
  vistaId?: string;
  position?: [number, number, number];
  yaw?: number;
  pitch?: number;
  era?: '1925' | '2026';
}

/**
 * Alias map for legacy vista IDs. The runtime vistaZones[].id surface moves
 * forward; legacy permalinks continue to resolve.
 *
 * If you rename a vista, add an entry here — it's the single point of truth
 * for the HOST-04 vista-id freeze contract.
 */
export const VISTA_ALIAS_MAP: Readonly<Record<string, string>> = Object.freeze({
  'vista-streetcar-stop': 'shore-south',
});

const WRITE_DEBOUNCE_MS = 500;

/**
 * Parse a URL hash into ParsedVistaState. Returns null for empty hashes. The
 * parser is permissive with vistaId values — alias resolution is the caller's
 * concern (see resolveVistaId below).
 */
export function parseVistaHash(hash: string): ParsedVistaState | null {
  if (!hash || hash === '#') return null;
  const q = new URLSearchParams(hash.replace(/^#/, ''));
  const state: ParsedVistaState = {};
  const v = q.get('v');
  if (v) state.vistaId = v;
  const c = q.get('c');
  if (c) {
    const parts = c.split(',').map(Number);
    if (parts.length >= 3 && parts.every(Number.isFinite)) {
      state.position = [parts[0], parts[1], parts[2]];
      if (parts.length >= 5 && Number.isFinite(parts[3]) && Number.isFinite(parts[4])) {
        state.yaw = parts[3];
        state.pitch = parts[4];
      }
    }
  }
  const e = q.get('e');
  if (e === '1925' || e === '2026') state.era = e;
  return Object.keys(state).length ? state : null;
}

/**
 * Encode ParsedVistaState into a hash string. Position coords round to 1
 * decimal (0.1m precision — sub-shoe resolution); angles round to 2 decimals
 * (0.01 rad = ~0.57°). Minimal-form with only era is supported for free-walk
 * before the first vista entry.
 */
export function encodeVistaHash(state: ParsedVistaState): string {
  // Manual key=value assembly rather than URLSearchParams — URLSearchParams
  // percent-encodes commas (%2C) in position/yaw/pitch values, which would
  // bloat the URL and visually obscure the schema. The keys we use (v, c, e)
  // and the values we emit (alphanumeric + dots + hyphens + commas) are all
  // safe characters per RFC 3986 fragment syntax; no encoding needed.
  const parts: string[] = [];
  if (state.vistaId) {
    parts.push(`v=${state.vistaId}`);
  } else if (state.position) {
    const [x, y, z] = state.position;
    const yaw = state.yaw ?? 0;
    const pitch = state.pitch ?? 0;
    parts.push(
      `c=${x.toFixed(1)},${y.toFixed(1)},${z.toFixed(1)},${yaw.toFixed(2)},${pitch.toFixed(2)}`,
    );
  }
  if (state.era) parts.push(`e=${state.era}`);
  return parts.length ? `#${parts.join('&')}` : '';
}

/**
 * Resolve a vista ID through the alias map. Returns the mapped ID if a legacy
 * alias is known; returns the input unchanged otherwise. Never throws — unknown
 * IDs fall back to the input so the caller can decide whether to flyTo
 * anyway or log + skip.
 */
export function resolveVistaId(id: string): string {
  return VISTA_ALIAS_MAP[id] ?? id;
}

/**
 * Effectful React hook. MUST be used inside an R3F Canvas (useThree + useFrame).
 * VistaPermalinkRig is the standard mount point.
 *
 * On mount: reads hash, restores era + vista/camera.
 * On frame: debounced write of current state back to hash.
 */
export function useVistaPermalink(): void {
  const camera = useThree((s) => s.camera);
  const era = useSceneStore((s) => s.era);
  const currentVistaId = useSceneStore((s) => s.currentVistaId);
  const setEra = useSceneStore((s) => s.setEra);
  const lastWrittenRef = useRef(0);

  // Read once on mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const parsed = parseVistaHash(window.location.hash);
    if (!parsed) return;
    if (parsed.era) setEra(parsed.era);
    if (parsed.vistaId) {
      const resolved = resolveVistaId(parsed.vistaId);
      const store = useSceneStore.getState() as unknown as {
        flyToVista?: (id: string) => void;
      };
      if (typeof store.flyToVista === 'function') {
        store.flyToVista(resolved);
      } else {
        // Graceful no-op — store does not (yet) expose flyToVista. The
        // permalink then restores only era; vista is left at whatever the
        // natural spawn pose places the camera. v1.1 can upgrade.
        console.warn(
          '[vista-permalink] flyToVista not exposed on useSceneStore; vista restore skipped for id:',
          resolved,
        );
      }
    } else if (parsed.position) {
      const [x, y, z] = parsed.position;
      camera.position.set(x, y, z);
      if (parsed.yaw !== undefined && parsed.pitch !== undefined) {
        camera.rotation.set(parsed.pitch, parsed.yaw, 0, 'YXZ');
      }
    }
  }, []);

  // Write every frame, debounced to 500ms.
  useFrame(() => {
    if (typeof window === 'undefined') return;
    const now = performance.now();
    if (now - lastWrittenRef.current < WRITE_DEBOUNCE_MS) return;
    lastWrittenRef.current = now;
    const state: ParsedVistaState = { era };
    if (currentVistaId) {
      state.vistaId = currentVistaId;
    } else {
      state.position = [camera.position.x, camera.position.y, camera.position.z];
      // Read yaw/pitch from camera rotation (YXZ order — the convention the
      // read path uses above). Roll stays implicit zero.
      state.yaw = camera.rotation.y;
      state.pitch = camera.rotation.x;
    }
    const newHash = encodeVistaHash(state);
    if (newHash && newHash !== window.location.hash) {
      window.history.replaceState(null, '', newHash);
    }
  });
}
